import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

import type { FilterOptionList } from '../../core/filters/filter-state.model';
import { FaIconComponent, faChevronDown, faXmark } from '../../ui/icons/app-icons.registry';

/**
 * A searchable single-select combobox, hand-rolled against the WAI-ARIA 1.2
 * combobox pattern (R-005): `role="combobox"` with `aria-expanded` and
 * `aria-controls` over a `role="listbox"`, `aria-activedescendant` for virtual
 * focus, type-ahead over the loaded options, and Escape to close and restore.
 *
 * `@angular/cdk` is deliberately absent — it was dropped in AITH-42 on a
 * measured initial-chunk decision (R-009), and this bar sits on the eager path
 * (R-012). The repository already substitutes hand-written equivalents for the
 * CDK (`LayoutService` for `BreakpointObserver`, the drawer's focus trap for
 * `cdkTrapFocus`); this is the third.
 *
 * **Pick-from-list only** (2026-08-24 clarification): typed text is a *filter*,
 * never a value. Nothing but selecting an option commits, so blur and Escape
 * both discard the query and restore the displayed selection. A value restored
 * from the URL that is not in the list is still displayed and still applied —
 * these are open sets (FR-006).
 */
/**
 * The reason a disabled selector states when its route declares none (GH-34 §1 D-8). One constant so
 * the bar's fallback for a route-declared reason (GH-164 D-11) cannot drift from it.
 */
export const DEFAULT_DISABLED_REASON = 'Not available here yet';

@Component({
  selector: 'app-searchable-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FaIconComponent],
  templateUrl: './searchable-select.component.html',
  styleUrl: './searchable-select.component.scss',
})
export class SearchableSelectComponent {
  /** Stable control name — the source of every DOM id and test id. */
  readonly name = input.required<string>();
  readonly label = input.required<string>();
  readonly options = input.required<FilterOptionList>();
  readonly value = input<string | null>(null);
  readonly placeholder = input<string>('All');
  /** Maps a stored value to its display text (the client selector shows a name, applies a code). */
  readonly displayFor = input<(value: string) => string>((value) => value);
  /** Overridable so the bar can explain the R-003 staff-with-no-client case specifically. */
  readonly unavailableMessage = input<string>('Options unavailable');

  /**
   * The GH-34 §1 **Disabled** treatment (D-3/D-8): the control renders and cannot be operated.
   *
   * It is **policy-driven, not data-driven**, which is the whole reason it is a separate input
   * rather than a reuse of the FR-016 `status === 'error'` state. On `/home` and
   * `/clients/:clientCode` those option lists genuinely populate for the caller, so rendering
   * "Options unavailable" there would be a lie — and it would conflate a real fetch failure with a
   * deliberate decision in both the specs and the telemetry. This input is honest on every page
   * regardless of whether the list resolves.
   *
   * What it must preserve (D-8 R2): the placeholder and the URL-restored value both still render,
   * because `displayText()` binds through `[value]` and a disabled `<input>` shows its value. That
   * is what answers `AITH-44/research.md:139`'s recorded objection — for this component, disabling
   * does not lose the restored value's display.
   */
  readonly disabled = input(false);

  /**
   * Why the control is inert, rendered in the always-visible {@link statusMessage} line.
   *
   * It cannot live only in `aria-describedby`: {@link disabled} removes the input from the tab
   * order, so nothing ever focuses the control and a focus-announced description is never read.
   *
   * **How it actually reaches a reader**, corrected after review (Copilot, `aith-ui` #35):
   *
   * 1. **On first render — as visible, document-order text.** The line sits immediately after its
   *    control, inside the same component, and the input points at it with `aria-describedby`. A
   *    screen reader traversing the page meets it in place.
   * 2. **When the treatment changes mid-session — through `role="status"`.** Navigating between
   *    routes with different declarations keeps the same bar instance and changes only this text,
   *    and a live region whose element is already mounted announces that.
   *
   * What `role="status"` does **not** do is announce content created in the same render as the
   * region itself, which is the first-render case above. This repository documents that exact
   * behaviour at `client-detail.page.html:9-14`. It needs no separate announcer here, and that is
   * a structural difference rather than an oversight: `client-detail`'s note lives inside an `@if`,
   * so the element itself comes and goes, whereas this line is rendered in **every** state
   * (SC-005 requires it, empty on success) so the region is permanently mounted.
   *
   * The earlier wording — "the only channel left once the control cannot take focus" — overstated
   * the live region's part and understated the plain text's.
   */
  readonly disabledReason = input<string>(DEFAULT_DISABLED_REASON);

  /**
   * Bumped by the bar on Reset and on the FR-019 session clear. The query is
   * *local* and has no external sync at all — typed text is a filter, never a
   * value (2026-08-24 clarification) — so without this token an uncommitted
   * query, and the popup it opened, survive both a Reset and a change of owner.
   */
  readonly resetToken = input(0);

  /**
   * GH-164 C-4: the label of the BLANK-segment choice, or `null` to offer none. It is offered only
   * when the list reports {@link FilterOptionList.unspecifiedObserved}, renders first and visually
   * distinct, and commits through {@link unspecifiedChange} — never as {@link valueChange} with the
   * text, because a real value can carry exactly that text.
   */
  readonly unspecifiedLabel = input<string | null>(null);

  /** Whether the blank-segment choice is the current selection. */
  readonly unspecifiedSelected = input(false);

  readonly valueChange = output<string | null>();

  /** Emitted when the reader chooses the blank-segment entry (GH-164 C-4). */
  readonly unspecifiedChange = output<void>();

  private readonly inputElement = viewChild.required<ElementRef<HTMLInputElement>>('comboboxInput');
  private readonly injector = inject(Injector);

  private readonly expanded = signal(false);
  /** `null` = not typing, so the field shows the selection; a string = an active filter query. */
  private readonly query = signal<string | null>(null);
  private readonly activeIndex = signal(-1);

  protected readonly chevronIcon = faChevronDown;
  protected readonly clearIcon = faXmark;

  protected readonly inputId = computed(() => `filter-${this.name()}-input`);
  protected readonly listboxId = computed(() => `filter-${this.name()}-listbox`);
  protected readonly statusId = computed(() => `filter-${this.name()}-status`);
  protected readonly testId = computed(() => `filter-${this.name()}`);

  /**
   * A popup is only ever opened over a ready, non-empty list (FR-016) — and never at all while the
   * control is {@link disabled}, which closes the keyboard and programmatic routes to the listbox
   * as well as the pointer one the `disabled` attribute already blocks.
   */
  protected readonly selectable = computed(() => !this.disabled() && this.options().status === 'success');
  protected readonly open = computed(() => this.expanded() && this.selectable());

  protected readonly displayText = computed(() => {
    const query = this.query();
    if (query !== null) return query;
    if (this.unspecifiedSelected()) return this.unspecifiedLabel() ?? '';
    const value = this.value();
    return value === null ? '' : this.displayFor()(value);
  });

  /** Whether anything is selected — a value or the blank-segment choice. */
  protected readonly hasSelection = computed(() => this.value() !== null || this.unspecifiedSelected());

  protected readonly visibleOptions = computed<readonly SelectEntry[]>(() => {
    const query = this.query()?.trim().toLowerCase() ?? '';
    const label = this.unspecifiedLabel();
    const entries: SelectEntry[] = [];
    if (label !== null && this.options().unspecifiedObserved === true
      && (query === '' || label.toLowerCase().includes(query))) {
      entries.push({ unspecified: true, value: '', text: label });
    }
    for (const value of this.options().values) {
      const text = this.displayFor()(value);
      if (query === '' || text.toLowerCase().includes(query)) entries.push({ unspecified: false, value, text });
    }
    return entries;
  });

  protected readonly activeOptionId = computed(() => {
    if (!this.open()) return null;
    const index = this.activeIndex();
    return index < 0 || index >= this.visibleOptions().length ? null : this.optionId(index);
  });

  /**
   * The FR-016 presentation. `success` renders an empty line rather than no
   * line at all, so the control's height is identical in every state and
   * arriving options shift nothing (SC-005).
   *
   * {@link disabled} takes precedence over every FR-016 state, because the reason the control is
   * inert is a decision about the *view* and outranks whatever the option list happens to be
   * doing. It is also the only reason the reader can act on: "Loading…" over a control nobody can
   * operate describes a fetch that will change nothing.
   */
  protected readonly statusMessage = computed(() => {
    if (this.disabled()) return this.disabledReason();
    // GH-164 C-4 (aith-ui #46 review): the field shows the same text a value named "Unspecified" would,
    // so the always-rendered status line says what the selection means — visible, and announced
    // through the input's aria-describedby. Review of aith-ui #45: the list's own state follows it
    // rather than being replaced by it, so `?appState=unspecified` restored over a failed options read
    // still says why the list will not open.
    const list = this.listStatusMessage();
    if (this.unspecifiedSelected()) {
      const description = this.unspecifiedDescription();
      return list === '' ? description : `${description}. ${list}`;
    }
    return list;
  });

  private listStatusMessage(): string {
    switch (this.options().status) {
      case 'loading':
        return 'Loading…';
      case 'empty':
        return 'No matches';
      case 'error':
        return this.unavailableMessage();
      case 'success':
        return '';
    }
  }

  constructor() {
    // The listbox scrolls (`max-height` + `overflow-y: auto` in the stylesheet)
    // while Arrow/Home/End move only `aria-activedescendant`, so virtual focus
    // could land on an off-screen option. The options render via `@for` *after*
    // the signal write lands, so the scroll is deferred to after the next
    // render — which also covers the keystroke that opens the popup and
    // activates an option in the same change-detection pass.
    effect(() => {
      const activeId = this.activeOptionId();
      if (activeId === null) return;
      afterNextRender(
        () => document.getElementById(activeId)?.scrollIntoView({ block: 'nearest' }),
        { injector: this.injector },
      );
    });

    // An explicit Reset (or session clear) discards the type-ahead query and
    // closes the popup. `value` changing cannot stand in for this: the query is
    // never published, so Reset over an already-null value writes nothing the
    // control could observe. The initial run is skipped so a deep-linked
    // restore's state is not wiped at construction.
    let lastResetToken = untracked(() => this.resetToken());
    effect(() => {
      const token = this.resetToken();
      if (token === lastResetToken) return;
      lastResetToken = token;
      untracked(() => this.close());
    });
  }

  protected optionId(index: number): string {
    return `filter-${this.name()}-option-${index}`;
  }

  protected onInput(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
    this.expanded.set(true);
    // Activate the first match so type-ahead + Enter commits without an arrow
    // press — and so an unmatched query activates nothing.
    this.activeIndex.set(this.visibleOptions().length > 0 ? 0 : -1);
  }

  protected onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(this.open() ? 1 : 0);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(this.open() ? -1 : 0);
        break;
      case 'Home':
        if (!this.open()) return;
        event.preventDefault();
        this.setActive(0);
        break;
      case 'End':
        if (!this.open()) return;
        event.preventDefault();
        this.setActive(this.visibleOptions().length - 1);
        break;
      case 'Enter': {
        if (!this.open()) return;
        event.preventDefault();
        const entry = this.visibleOptions()[this.activeIndex()];
        if (entry !== undefined) this.commit(entry);
        break;
      }
      case 'Escape':
        // Close and restore the displayed selection, keeping focus (ARIA 1.2).
        // Typed text is discarded, never committed.
        event.preventDefault();
        this.close();
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  protected onFocus(): void {
    this.expanded.set(true);
  }

  protected onBlur(): void {
    // Blur discards the query — an uncommitted filter is not a selection.
    this.close();
  }

  /** `mousedown` rather than `click`: the input's blur must not close the popup first. */
  protected onOptionMouseDown(event: MouseEvent, entry: SelectEntry): void {
    event.preventDefault();
    this.commit(entry);
  }

  /**
   * What the blank-segment choice selects, in words. Used as that entry's accessible name and as the
   * status line while it is selected, so a screen reader never hears two identical "Unspecified"
   * options when a real value carries that name (aith-ui #46 review).
   */
  protected unspecifiedDescription(): string {
    return `${this.unspecifiedLabel() ?? ''}: no ${this.label()} recorded`;
  }

  protected isSelected(entry: SelectEntry): boolean {
    return entry.unspecified ? this.unspecifiedSelected() : !this.unspecifiedSelected() && entry.value === this.value();
  }

  protected onFieldMouseDown(): void {
    this.expanded.update((expanded) => !expanded);
  }

  protected clear(): void {
    this.query.set(null);
    this.close();
    this.valueChange.emit(null);
  }

  private commit(entry: SelectEntry): void {
    this.query.set(null);
    this.close();
    if (entry.unspecified) {
      this.unspecifiedChange.emit();
    } else {
      this.valueChange.emit(entry.value);
    }
  }

  private close(): void {
    this.expanded.set(false);
    this.query.set(null);
    this.activeIndex.set(-1);
  }

  private moveActive(delta: number): void {
    if (!this.selectable()) return;
    this.expanded.set(true);
    const count = this.visibleOptions().length;
    if (count === 0) {
      this.activeIndex.set(-1);
      return;
    }
    const current = this.activeIndex();
    const next = current < 0 ? 0 : current + delta;
    this.setActive(Math.min(Math.max(next, 0), count - 1));
  }

  private setActive(index: number): void {
    this.activeIndex.set(index);
  }
}

/** One listbox entry: a value, or the blank-segment choice (GH-164 C-4). */
interface SelectEntry {
  readonly unspecified: boolean;
  readonly value: string;
  readonly text: string;
}
